import i18n from "i18next";

import { initReactI18next } from "react-i18next";

import Backend from "i18next-http-backend";
import LanguageDetector from "i18next-browser-languagedetector";

import translationEN from "./locales/en.json";
import translationKA from "./locales/ka.json";
import translationRU from "./locales/ru.json";
import translationES from "./locales/es.json";
import translationPT from "./locales/pt.json";
import translationPtBR from "./locales/pt-BR.json";
import translationFR from "./locales/fr.json";
import translationID from "./locales/id.json";
import translationKM from "./locales/km.json";
import translationMS from "./locales/ms.json";
import translationSR from "./locales/sr.json";
import translationTH from "./locales/th.json";
import translationTL from "./locales/tl.json";
import translationTR from "./locales/tr.json";
import translationVI from "./locales/vi.json";
import translationZH from "./locales/zh.json";
import translationBN from "./locales/bn.json";
import translationHI from "./locales/hi.json";
import translationJA from "./locales/ja.json";
import translationKO from "./locales/ko.json";
import translationLO from "./locales/lo.json";
import translationMM from "./locales/my.json";
import translationFil from "./locales/fil.json";
import translationBs from "./locales/bs.json";
import translationCnr from "./locales/cnr.json";
import translationDE from "./locales/de.json";
import ConfigService from "./services/ConfigService";
import cdnClient from "./api/cdnClient";

const supportedLngs = [
  "bn",
  "bs",
  "cnr",
  "de",
  "en",
  "es",
  "fil",
  "fr",
  "hi",
  "id",
  "ja",
  "ka",
  "km",
  "ko",
  "lo",
  "ms",
  "my",
  "pt-BR",
  "pt",
  "ru",
  "sr",
  "th",
  "tl",
  "tr",
  "vi",
  "zh",
];

const languageAliases: Record<string, string> = { ba: "bs", me: "cnr" };
const resolveLanguage = (lang: string): string => languageAliases[lang] || lang;

const fetchedLanguages = new Set();

const fetchRemoteTranslationFiles = async (currentLanguage: string) => {
  const resolvedLanguage = resolveLanguage(currentLanguage);
  if (fetchedLanguages.has(resolvedLanguage) || !ConfigService.getPromoId()) return;

  try {
    const response = await cdnClient.get(
      `/promo/${ConfigService.getPromoId()}/${resolvedLanguage}.json`,
      {
        headers: {
          "Cache-Control": "no-cache",
          Pragma: "no-cache",
        },
      }
    );

    i18n.addResourceBundle(resolvedLanguage, "translation", response.data);
    fetchedLanguages.add(resolvedLanguage);
  } catch (err) {
    if (resolvedLanguage !== "en" && !fetchedLanguages.has("en")) {
      try {
        const fallbackResponse = await cdnClient.get(
          `/promo/${ConfigService.getPromoId()}/en.json`,
          {
            headers: {
              "Cache-Control": "no-cache",
              Pragma: "no-cache",
            },
          }
        );
        i18n.addResourceBundle("en", "translation", fallbackResponse.data);
        fetchedLanguages.add("en");
      } catch (fallbackErr) {
        console.log(`couldn't find resources for fallback language (en)`);
      }
    }
  }
};

const resources = {
  en: {
    translation: translationEN,
  },
  de: {
    translation: translationDE,
  },
  ka: {
    translation: translationKA,
  },
  ru: {
    translation: translationRU,
  },
  es: {
    translation: translationES,
  },
  pt: {
    translation: translationPT,
  },
  "pt-BR": {
    translation: translationPtBR,
  },
  fr: {
    translation: translationFR,
  },
  id: {
    translation: translationID,
  },
  km: {
    translation: translationKM,
  },
  ms: {
    translation: translationMS,
  },
  sr: {
    translation: translationSR,
  },
  th: {
    translation: translationTH,
  },
  tl: {
    translation: translationTL,
  },
  tr: {
    translation: translationTR,
  },
  vi: {
    translation: translationVI,
  },
  zh: {
    translation: translationZH,
  },
  bn: {
    translation: translationBN,
  },
  hi: {
    translation: translationHI,
  },
  ja: {
    translation: translationJA,
  },
  ko: {
    translation: translationKO,
  },
  lo: {
    translation: translationLO,
  },
  my: {
    translation: translationMM,
  },
  fil: {
    translation: translationFil,
  },
  bs: {
    translation: translationBs,
  },
  cnr: {
    translation: translationCnr,
  },
};

const urlLng = resolveLanguage(ConfigService.getLng());

i18n
  .use(Backend)
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    supportedLngs: supportedLngs,
    ...(urlLng ? { lng: urlLng } : {}),
    fallbackLng: "en",
    detection: {
      order: ["querystring", "path", "cookie", "htmlTag"],
      caches: ["cookie"],
    },
    interpolation: {
      escapeValue: false,
    },
  })
  .then(() => {
    fetchRemoteTranslationFiles(i18n.language);
  });

i18n.on("languageChanged", (lang) => {
  fetchRemoteTranslationFiles(resolveLanguage(lang));
});

export default i18n;
